import { createContext, useContext, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { DemoWorld } from './demo/DemoWorld';
import { DEFAULT_MODE, DEMO_ENABLED, RUNNER_API } from './api/config';
import type { WorldActions, WorldView } from './types';

export interface WorldSource extends WorldActions {
  subscribe(fn: () => void): () => void;
  getView(): WorldView;
  setPaused?(p: boolean): void;
  quickTrade?(side: 'buy' | 'sell', size?: 'S' | 'M' | 'L'): Promise<unknown>;
  stop?(): void;
  /** Pause/resume background polling while another source is shown. */
  suspend?(): void;
  resume?(): void;
}

interface Ctx {
  source: WorldSource;
  mode: 'demo' | 'local';
  setMode(m: 'demo' | 'local'): void;
  localAvailable: boolean | null;
}

const WorldCtx = createContext<Ctx | null>(null);

/** Read API base (VITE_RUNNER_API, default http://127.0.0.1:8787). */
export const API_URL = RUNNER_API;

function initialMode(): 'demo' | 'local' {
  if (!DEMO_ENABLED) return 'local';
  const q = new URLSearchParams(window.location.search).get('mode');
  if (q === 'local' || q === 'demo') return q;
  try {
    const s = localStorage.getItem('runner.mode');
    if (s === 'local' || s === 'demo') return s;
  } catch {
    /* ignore */
  }
  return DEFAULT_MODE;
}

export function WorldProvider({ children, makeLocal }: { children: ReactNode; makeLocal?: () => WorldSource }) {
  const [mode, setModeState] = useState<'demo' | 'local'>(initialMode);
  const demoRef = useRef<DemoWorld | null>(null);
  const localRef = useRef<WorldSource | null>(null);

  // Demo mode makes no network requests at all (not even a health probe). Local-chain mode reports API
  // reachability itself, explicitly, once the user switches to it.
  const localAvailable: boolean | null = null;

  const source = useMemo<WorldSource>(() => {
    if (mode === 'local' && makeLocal) {
      if (!localRef.current) localRef.current = makeLocal();
      localRef.current.resume?.();
      return localRef.current;
    }
    localRef.current?.suspend?.();
    if (!demoRef.current) {
      const qs = new URLSearchParams(window.location.search);
      const seed = Number(qs.get('seed') ?? '') || undefined;
      // ?scenario=warmup starts the demo without its labelled sponsorship: runs wait for fee income (warm-up phase).
      demoRef.current = new DemoWorld({ seed, scenario: qs.get('scenario') === 'warmup' ? 'warmup' : 'default' });
    }
    return demoRef.current;
  }, [mode, makeLocal]);

  const setMode = (m: 'demo' | 'local') => {
    if (!DEMO_ENABLED && m === 'demo') return;
    try {
      localStorage.setItem('runner.mode', m);
    } catch {
      /* ignore */
    }
    setModeState(m);
  };

  return <WorldCtx.Provider value={{ source, mode, setMode, localAvailable }}>{children}</WorldCtx.Provider>;
}

export function useWorldCtx(): Ctx {
  const v = useContext(WorldCtx);
  if (!v) throw new Error('WorldProvider missing');
  return v;
}

/**
 * Subscribe to the active source's view (re-renders at most ~5 times per second; a notification within 200 ms of the
 * last refresh is coalesced into the next one). The snapshot is refreshed only inside the source's notification, so
 * getSnapshot returns the same object for every read between notifications, as useSyncExternalStore requires.
 */
export function useWorld(): { view: WorldView; source: WorldSource } {
  const { source } = useWorldCtx();
  const cache = useRef<{ source: WorldSource | null; v: WorldView | null; at: number }>({ source: null, v: null, at: 0 });
  // At most one view per 200 ms, but never drop the last change: an update inside the window is delivered when the
  // window ends (a wallet that answers instantly must not leave "Connecting…" on screen).
  const subscribe = useMemo(
    () => (fn: () => void) => {
      let timer: ReturnType<typeof setTimeout> | null = null;
      const flush = () => {
        timer = null;
        cache.current = { source, v: source.getView(), at: performance.now() };
        fn();
      };
      const unsubscribe = source.subscribe(() => {
        const wait = 200 - (performance.now() - cache.current.at);
        if (wait <= 0) flush();
        else if (!timer) timer = setTimeout(flush, wait);
      });
      return () => {
        if (timer) clearTimeout(timer);
        unsubscribe();
      };
    },
    [source],
  );
  const view = useSyncExternalStore(subscribe, () => {
    if (cache.current.source !== source || !cache.current.v) cache.current = { source, v: source.getView(), at: performance.now() };
    return cache.current.v!;
  });
  return { view, source };
}
